import { ref, computed } from 'vue';
import { format, differenceInDays } from 'date-fns';
import { useComponentI18n } from '@rdrive/i18n';
import messages from './messages/index.js';

// The table's initial sort: latest update first (not a column, so no header shows an arrow)
const DEFAULT_SORT = [{ key: 'issueLastUpdated', order: 'desc' }];

// The page-size menu (the table opens at 10 rows, which the menu does not offer — as before)
const PAGE_SIZES = [
    { title: '50', value: 50 },
    { title: '100', value: 100 },
    { title: '150', value: 150 },
    { title: '200', value: 200 }
];

export default (props) => {
    // LOCAL composer with the warn flags off (@rdrive/i18n). Before the move the grid read the
    // app's GLOBAL composer and its headers were raw keys DuckDbDataTable resolved through the
    // root; every string is now here. The search label and the phone table's texts are
    // DuckDbDataTable's own.
    const { t } = useComponentI18n(messages);

    // The columns — translated here (the table shows a `title` as-is). The keys are the sort keys
    // the app's fetchPage maps onto columns; the desktop row's cells follow this order.
    const headers = computed(() => [
        { title: t('id'), key: 'issueClientId', align: 'start', sortable: true },
        { title: t('photo'), key: 'photo', align: 'center', sortable: false },
        { title: t('issueDetails'), key: 'issueType', align: 'start', sortable: true, width: '300px' },
        { title: t('issueDescription'), key: 'issueDescription', align: 'start', sortable: true, width: '250px' },
        { title: t('responsibleParty'), key: 'responsibleParty', align: 'start', sortable: true, width: '150px' },
        { title: t('dueBy'), key: 'dueBy', align: 'start', sortable: true },
        { title: t('location'), key: 'issueLocationId', align: 'start', sortable: true },
        { title: t('statusInfo'), key: 'issueStatusId', align: 'start', sortable: true, width: '200px' },
    ]);

    // State
    const showSummaryReportDialog = ref(false);
    const showPhotoDialog = ref(false);
    const selectedPhotos = ref([]);

    // Photo dialog handler — which photos, in which order; the dialog (the sibling ItemPhotoDialog
    // since AM_ABSORPTION_PLAN step C5a) owns the carousel's position and starts it at the first
    // photo whenever it opens
    const openPhotoDialog = (item) => {
        const photos = [];

        // Check if both photo and responsePhoto exist
        if (item.photo && item.responsePhoto) {
            // Show carousel with both photos
            photos.push(item.photo);
            photos.push(item.responsePhoto);
        } else if (item.responsePhoto) {
            // Only response photo exists
            photos.push(item.responsePhoto);
        } else if (item.photo) {
            // Only main photo exists
            photos.push(item.photo);
        }

        selectedPhotos.value = photos;
        showPhotoDialog.value = true;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            // Check if the date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid date string:', dateString);
                return '';
            }
            return format(date, 'yyyy-MM-dd');
        } catch (error) {
            console.error('Error formatting date:', error, 'dateString:', dateString);
            return '';
        }
    };

    const getDaysLeft = (item) => {
        if (!item || !item.dueBy) return 0;
        try {
            const dueDate = new Date(item.dueBy);
            // Check if the date is valid
            if (isNaN(dueDate.getTime())) {
                console.warn('Invalid due date string:', item.dueBy);
                return 0;
            }
            let comparisonDate = new Date();
            if (item.isTerminal) {
                if (!item.updatedDate) {
                    console.warn('No updatedDate for terminal item:', item.id);
                    // Fallback to today if updatedDate is missing
                } else {
                    comparisonDate = new Date(item.updatedDate);
                    if (isNaN(comparisonDate.getTime())) {
                        console.warn('Invalid updatedDate string:', item.updatedDate);
                        comparisonDate = new Date(); // Fallback
                    }
                }
            }
            return differenceInDays(dueDate, comparisonDate);
        } catch (error) {
            console.error('Error calculating days left:', error, 'item:', item);
            return 0;
        }
    };

    const getDaysLeftText = (item) => {
        if (item.isTerminal) return '';
        const daysLeft = getDaysLeft(item);
        if (daysLeft === 0) return t('dueToday');
        if (daysLeft < 0) return `${Math.abs(daysLeft)} ${t('daysOverdue')}`;
        return `${daysLeft} ${t('daysLeft')}`;
    };

    // Every deep link goes through the app's opener (issue-analysis: `$launchUrl` in production)
    const openUrl = (url) => props.openUrl(url);

    const openItemUrl = (item) => {
        openUrl(`https://${props.apiHost}/${props.projectCode}/issues?issue-client-ids=${item.clientId}`);
    };

    // The dialog closes once the report is done — on success and on failure, as it always did
    const handleGenerateReport = async (options) => {
        if (!props.generateReport) return;
        await props.generateReport(options);
        showSummaryReportDialog.value = false;
    };

    // openUrl is the local wrapper, which shadows the prop of the same name (setup state wins
    // over props), as in ItemsGrid.
    return {
        t,
        headers,
        defaultSort: DEFAULT_SORT,
        pageSizes: PAGE_SIZES,
        showSummaryReportDialog,
        showPhotoDialog,
        selectedPhotos,
        openPhotoDialog,
        formatDate,
        getDaysLeft,
        getDaysLeftText,
        openUrl,
        openItemUrl,
        handleGenerateReport
    };
};
